import React, { useMemo, useState } from "react";
import { CgMenuRound } from "react-icons/cg";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  ComposedChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fetchGet, fetchPostSilance } from "../../fetchers";
import { Colors } from "../../assets/Colors";

// Endpoint monitoring procurement memakai LIKE '%'||param||'%', jadi nilai
// berikut berfungsi sebagai "semua":
//   unit   "U" -> semua BisnisUnitID (U0001, U0002, ...)
//   bagian "B" -> semua BagianID (B0001, ...)
//   bulan  "%" -> LIKE '%%%', satu-satunya cara mengambil setahun penuh
//                 (string kosong tidak bisa: segmen path kosong tak cocok route)
const SEMUA_UNIT = "U";
const SEMUA_BAGIAN = "B";
const SEMUA_BULAN = encodeURIComponent("%");
// Endpoint keuangan TIDAK memakai pola yang sama:
//   HutangJurnal & LabaRugi -> unit dibandingkan persis, "ALL" = semua unit
//   Mon/CashJournal         -> unit dibandingkan persis, tak ada nilai "semua"
//                              sehingga harus dipanggil per unit lalu digabung
const SEMUA_UNIT_KEUANGAN = "ALL";
// Filter bagian: HutangJurnal menerima `?divisi=<BagianID>` (kosong = semua).
// Sumber lain tidak punya parameter bagian, jadi disaring di sisi klien lewat
// NAMA bagian — sudah diverifikasi identik dengan MBagian."NamaBagian" di
// MSR (NamaBagian), PO (NamaKaryawan, lihat catatan kolom tertukar di bawah),
// dan cash journal (kolom "BagianID" yang isinya justru nama bagian).
const SEMUA_BAGIAN_OPSI = { value: "", label: "Semua Bagian" };
// Datanya dipakai untuk agregasi, bukan tabel — ambil sekaligus, jangan per halaman.
const LIMIT_AGREGASI = 5000;

const WARNA_STATUS = {
  Approved: "#22A06B",
  Diterima: "#0B7285",
  "Menunggu Review": "#F2B400",
  "Menunggu Approval": "#F18543",
  "Menunggu Dikirim": "#94A3B8",
  "MSR Belum Dikirim": "#94A3B8",
  Dibatalkan: "#DC2626",
};
const WARNA_DERET = ["#F18543", "#0B7285", "#22A06B", "#F2B400", "#7C3AED"];

const NAMA_BULAN = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "Mei",
  "Jun",
  "Jul",
  "Ags",
  "Sep",
  "Okt",
  "Nov",
  "Des",
];

const angka = (n) => new Intl.NumberFormat("id-ID").format(n || 0);

// Rupiah penuh untuk tooltip/kartu.
const rupiah = (n) =>
  `Rp ${new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(
    Number(n) || 0
  )}`;

// Rupiah ringkas untuk sumbu grafik — angka penuh membuat label saling tumpuk.
function rupiahRingkas(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${(v / 1e12).toFixed(1)} T`;
  if (abs >= 1e9) return `${(v / 1e9).toFixed(1)} M`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(0)} jt`;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(0)} rb`;
  return String(v);
}

const keAngka = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};

// Ambil bulan (1-12) dari tanggal ISO; kembalikan 0 bila tak terbaca.
function bulanDari(tanggal) {
  if (!tanggal) return 0;
  const bulan = parseInt(String(tanggal).slice(5, 7), 10);
  return Number.isNaN(bulan) ? 0 : bulan;
}

// PENTING — baris PO monitoring salah label di backend: urutan kolom SELECT tidak
// sejajar dengan struct PurchaseOrderMonitoringv2, sehingga nilainya tertukar:
//   NamaBagian   -> berisi NAMA SUPPLIER (mis. "CV. TOOLSINDO BERKAT SEJAHTERA")
//   NamaKaryawan -> berisi NAMA BAGIAN   (mis. "HRGA (Human Resource ...)")
//   NamaSupplier -> berisi nama PIC UserPC, bukan supplier
// Dibungkus di sini supaya kalau backend diperbaiki cukup ubah dua fungsi ini.
const supplierPO = (baris) => (baris?.NamaBagian || "").trim();
const bagianPO = (baris) => (baris?.NamaKaryawan || "").trim();

// Beban per bagian tidak ada di laporan laba rugi (yang hanya berdimensi unit),
// tapi bisa direkonstruksi: tiap bagian punya COA beban sendiri di
// MBagian."LawanBebanAccountID" (mis. FAP -> 5106002), dan jurnal pengeluaran
// barang mendebet akun itu dengan lawan Persediaan bagian pemilik stok
// (mis. 02066/GE/SO/07/2026: D 5106002 Beban FAP / K 1119003 Persediaan FM-ESM).
// Bagian yang dibebani = sisi DEBET, bukan sisi persediaan — sudah dicocokkan
// dengan GoodsExpenditure."Departemen" pada dokumen tersebut.
// Mutasi debet per akun untuk satu bulan diambil dari Accounting/TrialBalance.
const bulanSebelumnya = (bulan, tahun) => {
  const b = parseInt(bulan, 10);
  const t = parseInt(tahun, 10);
  return b <= 1
    ? { bulan: "12", tahun: String(t - 1) }
    : { bulan: String(b - 1), tahun: String(t) };
};

// Kategori beban dari respons Accounting/LabaRugi. Kunci yang sama muncul di
// grup BebanLangsung dan BebanAdministrasi — keduanya dijumlahkan per kategori.
const KATEGORI_BEBAN = [
  ["BebanKaryawan", "Karyawan"],
  ["BebanOperasi", "Operasi"],
  ["BebanAkomodasi", "Akomodasi"],
  ["BebanPelabuhan", "Pelabuhan"],
  ["BebanKantor", "Kantor"],
  ["BebanTransportasi", "Transportasi"],
  ["BebanAdministrasi", "Administrasi"],
  ["BebanProfesional", "Profesional"],
  ["BebanUmum", "Umum"],
  ["BebanUmumLain", "Umum Lain"],
  ["BebanMarketing", "Marketing"],
  ["BebanPenyusutan", "Penyusutan"],
  ["BebanPenyusutanAktiva", "Penyusutan Aktiva"],
  ["BebanAmortisasi", "Amortisasi"],
];

const potong = (teks, panjang = 28) =>
  teks && teks.length > panjang ? `${teks.slice(0, panjang)}…` : teks || "-";

function Kartu({ judul, nilai, keterangan, warna }) {
  return (
    <div className="flex-1 min-w-[10rem] m-2 p-4 bg-white border rounded shadow-sm">
      <div className="text-xs uppercase tracking-wide text-gray-500">
        {judul}
      </div>
      <div className="text-2xl font-semibold mt-1" style={{ color: warna }}>
        {nilai}
      </div>
      {keterangan && (
        <div className="text-xs text-gray-400 mt-1">{keterangan}</div>
      )}
    </div>
  );
}

function Panel({ judul, catatan, tinggi = 300, children }) {
  return (
    <div className="w-full lg:w-1/2 p-2">
      <div className="h-full bg-white border rounded shadow-sm p-4">
        <div className="text-sm font-semibold text-gray-700">{judul}</div>
        {catatan && <div className="text-xs text-gray-400 mb-2">{catatan}</div>}
        <div style={{ height: tinggi }} className="mt-2">
          {children}
        </div>
      </div>
    </div>
  );
}

function Kosong({ pesan }) {
  return (
    <div className="h-full flex items-center justify-center text-sm text-gray-400 text-center px-4">
      {pesan}
    </div>
  );
}

function JudulSeksi({ judul, keterangan }) {
  return (
    <div className="px-2 mt-6">
      <div className="text-lg font-semibold text-gray-800">{judul}</div>
      <div className="text-xs text-gray-400">{keterangan}</div>
    </div>
  );
}

const DashboardPage = ({}) => {
  const user = JSON.parse(localStorage.getItem("user"));

  // Gerbang izin — sama aturannya dengan MenuBuilder.js: 0 / null / undefined = tak boleh.
  // Catatan: Otoritas adalah snapshot localStorage saat login, jadi perubahan di
  // /setup-otoritas baru terlihat setelah user logout lalu login lagi.
  const bolehAnalisa =
    user?.Otoritas?.DashboardAnalisa !== 0 &&
    !!user?.Otoritas?.DashboardAnalisa;

  const sekarang = new Date();
  const tahunIni = sekarang.getFullYear();
  const [tahun, setTahun] = useState(String(tahunIni));
  const [bulan, setBulan] = useState(String(sekarang.getMonth() + 1));
  const [unit, setUnit] = useState("SEMUA");
  const [bagian, setBagian] = useState(SEMUA_BAGIAN_OPSI.value);

  const { data: dataBagian } = useQuery({
    queryKey: ["dashboard-bagian"],
    queryFn: () => fetchGet("getMinMasterBagianV2"),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  const opsiBagian = useMemo(
    () => [SEMUA_BAGIAN_OPSI, ...(dataBagian?.data ?? [])],
    [dataBagian]
  );

  // Nama bagian terpilih — dipakai untuk penyaringan sisi klien.
  const namaBagian = useMemo(
    () => opsiBagian.find((o) => o.value === bagian)?.label || "",
    [opsiBagian, bagian]
  );
  const adaFilterBagian = bagian !== "";

  // ------------------------------------------------------------------
  // Procurement — MSR & PO, setahun penuh (filter bulan tidak berlaku).
  // ------------------------------------------------------------------
  const { data: dataMSR, isLoading: muatMSR } = useQuery({
    queryKey: ["dashboard-msr", tahun],
    queryFn: () =>
      fetchGet(
        `Procurement/Monitoring/PurchaseRequisition/${SEMUA_UNIT}/${SEMUA_BULAN}/${tahun}/${SEMUA_BAGIAN}/?page=1&limit=${LIMIT_AGREGASI}`
      ),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  const { data: dataPO, isLoading: muatPO } = useQuery({
    queryKey: ["dashboard-po", tahun],
    queryFn: () =>
      fetchGet(
        `Procurement/Monitoring/PurchaseOrder/${SEMUA_UNIT}/${SEMUA_BULAN}/${tahun}/${SEMUA_BAGIAN}/?page=1&limit=${LIMIT_AGREGASI}`
      ),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  const barisMSR = useMemo(() => dataMSR?.data ?? [], [dataMSR]);
  const barisPO = useMemo(() => dataPO?.data ?? [], [dataPO]);

  // Daftar unit diambil dari data procurement (id + nama sekaligus), supaya
  // tidak perlu endpoint master tambahan tapi tetap punya ID untuk endpoint keuangan.
  const daftarUnit = useMemo(() => {
    const peta = new Map();
    [...barisMSR, ...barisPO].forEach((r) => {
      if (r.BisnisUnitID && !peta.has(r.BisnisUnitID)) {
        peta.set(r.BisnisUnitID, r.NamaBisnisUnit || r.BisnisUnitID);
      }
    });
    return Array.from(peta, ([id, nama]) => ({ id, nama })).sort((a, b) =>
      a.nama.localeCompare(b.nama)
    );
  }, [barisMSR, barisPO]);

  const unitTerpilih = unit === "SEMUA" ? null : unit;
  const cocokUnit = (r) => !unitTerpilih || r.BisnisUnitID === unitTerpilih;
  const cocokBagian = (nama) => !adaFilterBagian || nama === namaBagian;

  const msr = useMemo(
    () => barisMSR.filter((r) => cocokUnit(r) && cocokBagian(r.NamaBagian)),
    [barisMSR, unit, bagian, namaBagian]
  );
  const po = useMemo(
    () => barisPO.filter((r) => cocokUnit(r) && cocokBagian(bagianPO(r))),
    [barisPO, unit, bagian, namaBagian]
  );

  // ------------------------------------------------------------------
  // Keuangan — hutang, uang muka, beban. Kumulatif/periodik s/d bulan terpilih.
  // ------------------------------------------------------------------
  const unitKeuangan = unitTerpilih || SEMUA_UNIT_KEUANGAN;

  // Hutang satu-satunya yang disaring di SERVER: barisnya cuma berisi PO,
  // supplier, dan nominal — tak ada informasi bagian untuk disaring di klien.
  const { data: dataHutang, isLoading: muatHutang } = useQuery({
    queryKey: ["dashboard-hutang", unitKeuangan, bulan, tahun, bagian],
    queryFn: () =>
      fetchGet(
        `Accounting/Mon/HutangJurnal/${unitKeuangan}/${bulan}/${tahun}` +
          (bagian ? `?divisi=${encodeURIComponent(bagian)}` : "")
      ),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  // Mon/CashJournal membandingkan BisnisUnitID PERSIS dan tak punya nilai "semua",
  // jadi untuk "Semua Unit" endpoint dipanggil sekali per unit lalu digabung.
  const unitUntukKas = unitTerpilih
    ? [unitTerpilih]
    : daftarUnit.map((u) => u.id);

  const { data: dataKas, isLoading: muatKas } = useQuery({
    queryKey: ["dashboard-kas", unitUntukKas.join(","), tahun],
    queryFn: async () => {
      const hasil = await Promise.all(
        unitUntukKas.map((id) =>
          fetchGet(
            `Accounting/Mon/CashJournal/${id}/NonPO/${SEMUA_BULAN}/${tahun}?page=1&limit=${LIMIT_AGREGASI}`
          )
        )
      );
      return hasil.flatMap((r) => r?.data ?? []);
    },
    enabled: bolehAnalisa && unitUntukKas.length > 0,
    staleTime: 300000,
  });

  const periodeLalu = bulanSebelumnya(bulan, tahun);

  const { data: dataTBKini, isLoading: muatTBKini } = useQuery({
    queryKey: ["dashboard-tb", unitKeuangan, bulan, tahun],
    queryFn: () =>
      fetchPostSilance("Accounting/TrialBalance", {
        BisnisUnitID: unitKeuangan,
        Bulan: bulan,
        Tahun: tahun,
      }),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  const { data: dataTBLalu, isLoading: muatTBLalu } = useQuery({
    queryKey: ["dashboard-tb", unitKeuangan, periodeLalu.bulan, periodeLalu.tahun],
    queryFn: () =>
      fetchPostSilance("Accounting/TrialBalance", {
        BisnisUnitID: unitKeuangan,
        Bulan: periodeLalu.bulan,
        Tahun: periodeLalu.tahun,
      }),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  const { data: dataLabaRugi, isLoading: muatLabaRugi } = useQuery({
    queryKey: ["dashboard-labarugi", unitKeuangan, bulan, tahun],
    queryFn: () =>
      fetchPostSilance("Accounting/LabaRugi", {
        BisnisUnitID: unitKeuangan,
        Bulan: bulan,
        Tahun: tahun,
      }),
    enabled: bolehAnalisa,
    staleTime: 300000,
  });

  // ------------------------------------------------------------------
  // Agregasi
  // ------------------------------------------------------------------
  const perStatus = useMemo(() => {
    const hitung = {};
    msr.forEach((r) => {
      const s = r.STATUS || "Tidak diketahui";
      hitung[s] = (hitung[s] || 0) + 1;
    });
    return Object.entries(hitung)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [msr]);

  const perBulan = useMemo(() => {
    const kosong = NAMA_BULAN.map((nama) => ({ name: nama, MSR: 0, PO: 0 }));
    msr.forEach((r) => {
      const b = bulanDari(r.DatePR);
      if (b >= 1 && b <= 12) kosong[b - 1].MSR += 1;
    });
    po.forEach((r) => {
      const b = bulanDari(r.TanggalPO);
      if (b >= 1 && b <= 12) kosong[b - 1].PO += 1;
    });
    return kosong;
  }, [msr, po]);

  const perBagian = useMemo(() => {
    const hitung = {};
    msr.forEach((r) => {
      const nama = (r.NamaBagian || "-").trim();
      hitung[nama] = (hitung[nama] || 0) + 1;
    });
    return Object.entries(hitung)
      .map(([nama, total]) => ({ name: potong(nama), penuh: nama, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [msr]);

  const perSupplier = useMemo(() => {
    const hitung = {};
    po.forEach((r) => {
      const nama = supplierPO(r) || "(tanpa supplier)";
      hitung[nama] = (hitung[nama] || 0) + 1;
    });
    return Object.entries(hitung)
      .map(([nama, total]) => ({ name: potong(nama), penuh: nama, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [po]);

  const ringkasan = useMemo(() => {
    const menunggu = msr.filter((r) =>
      String(r.STATUS || "").startsWith("Menunggu")
    ).length;
    const dibatalkan = msr.filter((r) => r.STATUS === "Dibatalkan").length;
    const rasio = msr.length ? Math.round((po.length / msr.length) * 100) : 0;
    return { menunggu, dibatalkan, rasio };
  }, [msr, po]);

  const barisHutang = useMemo(() => dataHutang?.data ?? [], [dataHutang]);

  const totalHutang = useMemo(() => {
    let total = 0;
    let terbayar = 0;
    let sisa = 0;
    barisHutang.forEach((r) => {
      total += keAngka(r.TotalHutang);
      terbayar += keAngka(r.Terbayar);
      sisa += keAngka(r.Sisa);
    });
    return { total, terbayar, sisa, jumlahPO: barisHutang.length };
  }, [barisHutang]);

  const hutangPerSupplier = useMemo(() => {
    const hitung = {};
    barisHutang.forEach((r) => {
      const nama = (r.DibayarKepada || "(tanpa nama)").trim();
      hitung[nama] = (hitung[nama] || 0) + keAngka(r.Sisa);
    });
    return Object.entries(hitung)
      .map(([nama, total]) => ({ name: potong(nama), penuh: nama, total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [barisHutang]);

  // Uang muka = cash journal NonPO ber-JenisTransaksi "Downpayment PO".
  // Kolom "BagianID" pada respons ini berisi NAMA bagian (COALESCE ke
  // 'Tanpa Bagian'), bukan ID — mayoritas uang muka memang tak berbagian.
  const uangMukaSemua = useMemo(
    () => (dataKas ?? []).filter((r) => r.JenisTransaksi === "Downpayment PO"),
    [dataKas]
  );
  const uangMuka = useMemo(
    () => uangMukaSemua.filter((r) => cocokBagian(r.BagianID)),
    [uangMukaSemua, bagian, namaBagian]
  );
  const uangMukaTanpaBagian = useMemo(
    () => uangMukaSemua.filter((r) => !r.BagianID || r.BagianID === "Tanpa Bagian").length,
    [uangMukaSemua]
  );

  const uangMukaPerBulan = useMemo(() => {
    const kosong = NAMA_BULAN.map((nama) => ({ name: nama, nilai: 0 }));
    uangMuka.forEach((r) => {
      const b = bulanDari(r.Tanggal);
      if (b >= 1 && b <= 12) kosong[b - 1].nilai += keAngka(r.Nominal);
    });
    return kosong;
  }, [uangMuka]);

  const uangMukaPerStatus = useMemo(() => {
    const hitung = {};
    uangMuka.forEach((r) => {
      const s = r.Status || "Tidak diketahui";
      hitung[s] = (hitung[s] || 0) + keAngka(r.Nominal);
    });
    return Object.entries(hitung)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [uangMuka]);

  const ringkasanUangMuka = useMemo(() => {
    const total = uangMuka.reduce((a, r) => a + keAngka(r.Nominal), 0);
    // IsClose = uang muka yang sudah cair DAN sudah dipotongkan di PembayaranAP approved.
    const belumTuntas = uangMuka.filter((r) => !r.IsClose);
    return {
      total,
      jumlah: uangMuka.length,
      belumTuntas: belumTuntas.length,
      nilaiBelumTuntas: belumTuntas.reduce(
        (a, r) => a + keAngka(r.Nominal),
        0
      ),
    };
  }, [uangMuka]);

  // Mutasi bersih akun beban satu bulan: Debet − Kredit (kredit = pembalikan).
  const mutasiAkun = (respons) => {
    const peta = {};
    (respons?.data ?? []).forEach((r) => {
      peta[r.DetailAccountID] = keAngka(r.Debet) - keAngka(r.Kredit);
    });
    return peta;
  };

  const bebanSemuaBagian = useMemo(() => {
    const kini = mutasiAkun(dataTBKini);
    const lalu = mutasiAkun(dataTBLalu);
    return opsiBagian
      .filter((b) => b.value && b.LawanBebanAccountID)
      .map((b) => ({
        id: b.value,
        name: potong(b.label, 24),
        penuh: `${b.label} — ${b.LawanBebanAccountID}`,
        kini: kini[b.LawanBebanAccountID] || 0,
        lalu: lalu[b.LawanBebanAccountID] || 0,
      }))
      .filter((r) => r.kini !== 0 || r.lalu !== 0)
      .sort((a, b) => b.kini - a.kini || b.lalu - a.lalu);
  }, [dataTBKini, dataTBLalu, opsiBagian]);

  const bebanPerBagian = useMemo(
    () =>
      adaFilterBagian
        ? bebanSemuaBagian.filter((r) => r.id === bagian)
        : bebanSemuaBagian,
    [bebanSemuaBagian, bagian, adaFilterBagian]
  );

  // Beban bagian terpilih (untuk kartu) vs total seluruh bagian (untuk cakupan).
  const totalBebanBagian = useMemo(
    () => bebanPerBagian.reduce((a, r) => a + r.kini, 0),
    [bebanPerBagian]
  );
  const totalBebanSemuaBagian = useMemo(
    () => bebanSemuaBagian.reduce((a, r) => a + r.kini, 0),
    [bebanSemuaBagian]
  );

  const beban = useMemo(() => {
    const isi = dataLabaRugi?.data;
    if (!isi) return { baris: [], totalKini: 0, totalLalu: 0, label: {} };

    const jumlahkan = (periode) => {
      const grup = isi[periode] || {};
      // BebanLangsung & BebanAdministrasi punya kunci yang sama (mis. BebanKantor)
      // — jumlahkan keduanya, jangan saling timpa lewat spread.
      const hasil = {};
      KATEGORI_BEBAN.forEach(([kunci]) => {
        hasil[kunci] =
          Number(grup.BebanLangsung?.[kunci] || 0) +
          Number(grup.BebanAdministrasi?.[kunci] || 0);
      });
      hasil.__jumlah =
        Number(grup.BebanLangsung?.JumlahBebanLangsung || 0) +
        Number(grup.BebanAdministrasi?.JumlahBebanAdministrasi || 0);
      return hasil;
    };

    const kini = jumlahkan("Current");
    const lalu = jumlahkan("Previous");

    const baris = KATEGORI_BEBAN.map(([kunci, label]) => ({
      name: label,
      kini: kini[kunci] || 0,
      lalu: lalu[kunci] || 0,
    }))
      .filter((r) => r.kini > 0 || r.lalu > 0)
      .sort((a, b) => b.kini - a.kini || b.lalu - a.lalu);

    return {
      baris,
      totalKini: kini.__jumlah || 0,
      totalLalu: lalu.__jumlah || 0,
      label: {
        kini: isi.CurrentPeriod || "Bulan ini",
        lalu: isi.PreviousPeriod || "Bulan lalu",
      },
    };
  }, [dataLabaRugi]);

  // ------------------------------------------------------------------
  // Daftar proses per jabatan — perilaku lama halaman ini, dipertahankan.
  // ------------------------------------------------------------------
  let dataArray = [];
  if (user?.Jabatan === "Logistic") {
    dataArray = [
      { name: "Permintaan Barang Baru" },
      { name: "Permintaan Barang dari Gudang" },
      { name: "Perbandingan Harga" },
      { name: "Proses PO" },
      { name: "Penerimaan Barang (BAST)" },
      { name: "Pengembalian Barang ke Supplier" },
      { name: "Pengeluaran Barang" },
    ];
  } else if (user?.Jabatan === "Finance") {
    dataArray = [
      { name: "Penagihan Hutan (AR)" },
      { name: "Pembayaran Hutang (AP)" },
      { name: "Penerimaan Kas" },
      { name: "Penerimaan Bank" },
    ];
  } else if (user?.Jabatan === "Accounting") {
    dataArray = [{ name: "Jurnal" }, { name: "Tutup Buku" }];
  }

  // Mayoritas cash journal tak mengisi BagianID, jadi filter bagian hampir selalu
  // mengosongkan grafik uang muka — jelaskan sebabnya, jangan biarkan tampak rusak.
  const pesanUangMukaKosong = adaFilterBagian
    ? `Tidak ada uang muka bagian ${namaBagian} pada tahun ${tahun}. ${angka(
        uangMukaTanpaBagian
      )} dari ${angka(
        uangMukaSemua.length
      )} dokumen uang muka tahun ini memang tidak mencantumkan bagian.`
    : `Tidak ada uang muka pada tahun ${tahun}`;

  const muatProcurement = muatMSR || muatPO;
  const muatKeuangan =
    muatHutang || muatKas || muatLabaRugi || muatTBKini || muatTBLalu;
  const adaProcurement = msr.length > 0 || po.length > 0;

  return (
    <div className="md:ml-64 mt-2">
      {dataArray.length > 0 && (
        <div className="mb-4">
          {dataArray.map((item, index) => (
            <div
              key={`proses-${index}`}
              className="flex flex-row ml-4 mt-2 cursor-pointer"
            >
              <CgMenuRound className="text-xl text-gray-800 mr-2" />
              {item.name}
            </div>
          ))}
        </div>
      )}

      {bolehAnalisa && (
        <div className="px-2 pb-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between px-2 mt-2">
            <div>
              <div className="text-xl font-semibold text-gray-800">
                Dashboard Analisa
              </div>
              <div className="text-xs text-gray-400">
                Procurement setahun penuh; keuangan mengikuti bulan yang dipilih
              </div>
            </div>
            <div className="flex flex-row items-center mt-3 sm:mt-0">
              <select
                value={tahun}
                onChange={(e) => setTahun(e.target.value)}
                className="border rounded py-2 px-3 text-sm mr-2 bg-white"
              >
                {[0, 1, 2].map((mundur) => {
                  const t = String(tahunIni - mundur);
                  return (
                    <option key={t} value={t}>
                      Tahun {t}
                    </option>
                  );
                })}
              </select>
              <select
                value={bulan}
                onChange={(e) => setBulan(e.target.value)}
                className="border rounded py-2 px-3 text-sm mr-2 bg-white"
              >
                {NAMA_BULAN.map((nama, i) => (
                  <option key={nama} value={String(i + 1)}>
                    {nama}
                  </option>
                ))}
              </select>
              <select
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                className="border rounded py-2 px-3 text-sm mr-2 bg-white"
              >
                <option value="SEMUA">Semua Unit</option>
                {daftarUnit.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nama}
                  </option>
                ))}
              </select>
              <select
                value={bagian}
                onChange={(e) => setBagian(e.target.value)}
                className="border rounded py-2 px-3 text-sm bg-white max-w-[16rem]"
              >
                {opsiBagian.map((b) => (
                  <option key={b.value || "semua"} value={b.value}>
                    {b.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* ============================ PROCUREMENT ============================ */}
          <JudulSeksi
            judul="Procurement"
            keterangan={
              `MSR & Purchase Order sepanjang tahun ${tahun}` +
              (adaFilterBagian ? ` — bagian ${namaBagian}` : "")
            }
          />

          {muatProcurement ? (
            <div className="p-8 text-center text-gray-400">
              Memuat data procurement…
            </div>
          ) : !adaProcurement ? (
            <div className="p-8 text-center text-gray-400">
              Tidak ada MSR maupun PO pada tahun {tahun}
              {adaFilterBagian ? ` untuk bagian ${namaBagian}` : ""}.
            </div>
          ) : (
            <>
              <div className="flex flex-row flex-wrap mt-2">
                <Kartu
                  judul="Total MSR"
                  nilai={angka(msr.length)}
                  keterangan={`Tahun ${tahun}`}
                  warna={Colors.primary}
                />
                <Kartu
                  judul="MSR Menunggu"
                  nilai={angka(ringkasan.menunggu)}
                  keterangan="Review + approval"
                  warna="#F2B400"
                />
                <Kartu
                  judul="MSR Dibatalkan"
                  nilai={angka(ringkasan.dibatalkan)}
                  keterangan="Status Dibatalkan"
                  warna="#DC2626"
                />
                <Kartu
                  judul="Total PO"
                  nilai={angka(po.length)}
                  keterangan={`${ringkasan.rasio}% dari jumlah MSR`}
                  warna="#0B7285"
                />
              </div>

              <div className="flex flex-row flex-wrap mt-2">
                <Panel
                  judul="MSR per Status"
                  catatan="Status dihitung backend dari Kirim / Approved / PRComparison"
                >
                  {perStatus.length === 0 ? (
                    <Kosong pesan="Tidak ada MSR" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={perStatus}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={95}
                          paddingAngle={2}
                        >
                          {perStatus.map((baris, i) => (
                            <Cell
                              key={`status-${baris.name}`}
                              fill={
                                WARNA_STATUS[baris.name] ||
                                WARNA_DERET[i % WARNA_DERET.length]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(nilai, nama) => [
                            `${angka(nilai)} MSR`,
                            nama,
                          ]}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Panel>

                <Panel
                  judul="Tren Bulanan MSR vs PO"
                  catatan="MSR memakai tanggal MSR, PO memakai tanggal PO"
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={perBulan}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={(nilai) => angka(nilai)} />
                      <Legend />
                      <Bar dataKey="MSR" fill={Colors.primary} barSize={18} />
                      <Line
                        type="monotone"
                        dataKey="PO"
                        stroke="#0B7285"
                        strokeWidth={2}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Panel>

                <Panel
                  judul="8 Bagian dengan MSR Terbanyak"
                  catatan="Jumlah MSR, bukan nilai rupiah"
                >
                  {perBagian.length === 0 ? (
                    <Kosong pesan="Tidak ada MSR" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={perBagian}
                        layout="vertical"
                        margin={{ left: 60, right: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={150}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          formatter={(nilai) => [`${angka(nilai)} MSR`, "Total"]}
                          labelFormatter={(_, muatan) =>
                            muatan?.[0]?.payload?.penuh || ""
                          }
                        />
                        <Bar dataKey="total" fill={Colors.primary} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>

                <Panel
                  judul="8 Supplier dengan PO Terbanyak"
                  catatan="Jumlah PO per supplier"
                >
                  {perSupplier.length === 0 ? (
                    <Kosong pesan="Tidak ada PO" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={perSupplier}
                        layout="vertical"
                        margin={{ left: 60, right: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={150}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          formatter={(nilai) => [`${angka(nilai)} PO`, "Total"]}
                          labelFormatter={(_, muatan) =>
                            muatan?.[0]?.payload?.penuh || ""
                          }
                        />
                        <Bar dataKey="total" fill="#0B7285" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>
              </div>
            </>
          )}

          {/* ============================= KEUANGAN ============================= */}
          <JudulSeksi
            judul="Keuangan"
            keterangan={
              `Hutang & beban s/d ${NAMA_BULAN[parseInt(bulan, 10) - 1]} ${tahun}; ` +
              `uang muka sepanjang tahun ${tahun}` +
              (adaFilterBagian
                ? ` — bagian ${namaBagian}, kecuali beban yang tetap se-unit`
                : "")
            }
          />

          {muatKeuangan ? (
            <div className="p-8 text-center text-gray-400">
              Memuat data keuangan…
            </div>
          ) : (
            <>
              <div className="flex flex-row flex-wrap mt-2">
                <Kartu
                  judul="Sisa Hutang PO"
                  nilai={rupiah(totalHutang.sisa)}
                  keterangan={`${angka(totalHutang.jumlahPO)} PO belum lunas`}
                  warna="#DC2626"
                />
                <Kartu
                  judul="Sudah Terbayar"
                  nilai={rupiah(totalHutang.terbayar)}
                  keterangan={`dari ${rupiah(totalHutang.total)} total hutang`}
                  warna="#22A06B"
                />
                <Kartu
                  judul="Uang Muka"
                  nilai={rupiah(ringkasanUangMuka.total)}
                  keterangan={`${angka(ringkasanUangMuka.jumlah)} dokumen, ${angka(
                    ringkasanUangMuka.belumTuntas
                  )} belum tuntas`}
                  warna="#7C3AED"
                />
                {adaFilterBagian ? (
                  <Kartu
                    judul={`Beban ${namaBagian}`}
                    nilai={rupiah(totalBebanBagian)}
                    keterangan={`${beban.label.kini || "Bulan ini"} · dari akun ${
                      opsiBagian.find((o) => o.value === bagian)
                        ?.LawanBebanAccountID || "-"
                    }`}
                    warna={Colors.primary}
                  />
                ) : (
                  <Kartu
                    judul={`Beban ${beban.label.kini || ""}`}
                    nilai={rupiah(beban.totalKini)}
                    keterangan={`${beban.label.lalu || "Bulan lalu"}: ${rupiah(
                      beban.totalLalu
                    )}`}
                    warna={Colors.primary}
                  />
                )}
              </div>

              <div className="flex flex-row flex-wrap mt-2">
                <Panel
                  judul="8 Supplier dengan Sisa Hutang Terbesar"
                  catatan="Hutang PO dari jurnal − pembayaran AP yang sudah approved"
                >
                  {hutangPerSupplier.length === 0 ? (
                    <Kosong pesan="Tidak ada hutang PO yang belum lunas pada periode ini" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={hutangPerSupplier}
                        layout="vertical"
                        margin={{ left: 60, right: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={rupiahRingkas} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={150}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          formatter={(nilai) => [rupiah(nilai), "Sisa hutang"]}
                          labelFormatter={(_, muatan) =>
                            muatan?.[0]?.payload?.penuh || ""
                          }
                        />
                        <Bar dataKey="total" fill="#DC2626" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>

                <Panel
                  judul="Uang Muka per Bulan"
                  catatan="Cash journal Non-PO berjenis Downpayment PO"
                >
                  {ringkasanUangMuka.jumlah === 0 ? (
                    <Kosong pesan={pesanUangMukaKosong} />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={uangMukaPerBulan}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={rupiahRingkas} />
                        <Tooltip
                          formatter={(nilai) => [rupiah(nilai), "Uang muka"]}
                        />
                        <Bar dataKey="nilai" fill="#7C3AED" barSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>

                <Panel
                  judul="Uang Muka per Status"
                  catatan="Nilai rupiah, bukan jumlah dokumen"
                >
                  {uangMukaPerStatus.length === 0 ? (
                    <Kosong pesan={pesanUangMukaKosong} />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={uangMukaPerStatus}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={95}
                          paddingAngle={2}
                        >
                          {uangMukaPerStatus.map((baris, i) => (
                            <Cell
                              key={`um-${baris.name}`}
                              fill={
                                WARNA_STATUS[baris.name] ||
                                WARNA_DERET[i % WARNA_DERET.length]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(nilai, nama) => [rupiah(nilai), nama]}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Panel>

                <Panel
                  judul="Komposisi Beban"
                  catatan={
                    `${beban.label.kini || "Bulan ini"} vs ${
                      beban.label.lalu || "bulan lalu"
                    } — beban langsung + administrasi` +
                    (adaFilterBagian
                      ? ". Laporan laba rugi hanya berdimensi unit; rincian per bagian ada di panel sebelah."
                      : "")
                  }
                >
                  {beban.baris.length === 0 ? (
                    <Kosong pesan="Tidak ada beban pada periode ini" />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={beban.baris}
                        layout="vertical"
                        margin={{ left: 40, right: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={rupiahRingkas} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={110}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip formatter={(nilai) => rupiah(nilai)} />
                        <Legend />
                        <Bar
                          dataKey="kini"
                          name={beban.label.kini || "Bulan ini"}
                          fill={Colors.primary}
                        />
                        <Bar
                          dataKey="lalu"
                          name={beban.label.lalu || "Bulan lalu"}
                          fill="#94A3B8"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>

                <Panel
                  judul="Beban per Bagian"
                  catatan={
                    "Mutasi debet COA beban milik tiap bagian — sebagian besar dari jurnal pengeluaran barang: D Beban bagian / K Persediaan. " +
                    (beban.totalKini > 0
                      ? `Mencakup ${rupiah(totalBebanSemuaBagian)} dari total beban ${rupiah(
                          beban.totalKini
                        )} (${Math.round(
                          (totalBebanSemuaBagian / beban.totalKini) * 100
                        )}%); sisanya beban umum seperti gaji & BBM yang tak terikat bagian.`
                      : "")
                  }
                >
                  {bebanPerBagian.length === 0 ? (
                    <Kosong
                      pesan={`Tidak ada mutasi beban bagian pada ${
                        beban.label.kini || "periode ini"
                      }`}
                    />
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={bebanPerBagian}
                        layout="vertical"
                        margin={{ left: 40, right: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" tickFormatter={rupiahRingkas} />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={140}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip
                          formatter={(nilai) => rupiah(nilai)}
                          labelFormatter={(_, muatan) =>
                            muatan?.[0]?.payload?.penuh || ""
                          }
                        />
                        <Legend />
                        <Bar
                          dataKey="kini"
                          name={beban.label.kini || "Bulan ini"}
                          fill="#22A06B"
                        />
                        <Bar
                          dataKey="lalu"
                          name={beban.label.lalu || "Bulan lalu"}
                          fill="#94A3B8"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Panel>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
